/**
 * localStorage outbox — the reason abandonment is measurable at all.
 *
 * `pwa_logs` has no offline queue: a row is an `addDoc` promise, and a page that
 * dies mid-flight simply loses it. That is not an edge case for this schema, it
 * is the common case for the rows we most want. `phase.exit` with
 * `outcome: "abandoned"` is emitted from `pagehide` — precisely the moment a
 * Firestore write is least likely to land. Without an outbox, every abandoned
 * session would look like a session with no exit row, and the abandonment rate
 * would read as zero.
 *
 * CHANGELOG [2.5.4] already works around this for one specific path with a
 * bounded 1.5s flush before a deliberate reload. This generalises it.
 *
 * DESIGN — why a "pending set" rather than a write-ahead log:
 *
 * The obvious approach is to persist every row to localStorage before writing it
 * and remove it on success. That is correct and far too expensive: localStorage
 * is synchronous and main-thread, and at ~10k rows/day on a device already
 * running TF.js pose detection, two blocking storage ops per log call is real
 * jank. See PLAN.md §11.1 — nothing in this system may add main-thread work to
 * the scan path.
 *
 * Instead: rows in flight are held in memory, and localStorage is touched
 * **once**, at `pagehide`/`visibilitychange:hidden`, for whatever has not
 * resolved. Steady-state cost is zero.
 *
 * IDEMPOTENCY: a row whose write actually landed but whose promise had not
 * resolved before unload is persisted and re-sent on the next boot. That would
 * duplicate under `addDoc`, so the row id is generated on the client and the
 * write uses `setDoc` — a re-send overwrites the same document instead of
 * creating a second one. Ids are random (not sequential), so this does not
 * reintroduce the key-hotspotting that `addDoc`'s random ids exist to avoid.
 *
 * A drained row keeps its original `timestamp`, `seq`, `pageLoadId` and
 * `journeyPageLoad`, so it stitches back into the correct position in the
 * correct journey. Its `serverTs` is necessarily the drain time, not the emit
 * time — which is exactly why PLAN.md §4.2 says never to order a journey by
 * `serverTs`.
 */

const OUTBOX_KEY = "ttf.telemetry.outbox.v1";

/** Hard caps so a permanently-offline device cannot fill the origin's quota. */
const MAX_ROWS = 200;
const MAX_BYTES = 100 * 1024;

export interface PendingRow {
  /** Client-generated Firestore document id. Makes a re-send idempotent. */
  id: string;
  /** The document body, already sanitized. `serverTs` is re-stamped on drain. */
  row: Record<string, unknown>;
}

/** Rows whose write has been started but not confirmed. In memory only. */
const pending = new Map<string, Record<string, unknown>>();

export function markPending(id: string, row: Record<string, unknown>): void {
  pending.set(id, row);
}

export function markSettled(id: string): void {
  pending.delete(id);
}

/** Test-only. */
export function __pendingSize(): number {
  return pending.size;
}

function readRaw(): PendingRow[] {
  try {
    const raw = window.localStorage.getItem(OUTBOX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Corrupt or unreadable — treat as empty rather than throwing. A lost
    // outbox is a gap in the data; a throw here is a broken app.
    return [];
  }
}

/**
 * Serialize whatever is still in flight. Called from `pagehide` and
 * `visibilitychange:hidden`, so it MUST be synchronous — the page may not get
 * another task. Merges with anything already persisted (a tab can be hidden and
 * restored several times before it finally dies).
 *
 * Drops OLDEST first when over cap: the newest rows are the ones nearest the
 * failure being investigated.
 */
export function persistPending(): void {
  if (pending.size === 0) return;
  try {
    const existing = readRaw();
    const merged = existing.concat(
      Array.from(pending.entries()).map(([id, row]) => ({ id, row }))
    );

    // De-dupe by id — a row can be persisted on a hide, survive, then be
    // persisted again on the real unload.
    const byId = new Map<string, PendingRow>();
    for (const entry of merged) byId.set(entry.id, entry);
    let rows = Array.from(byId.values());

    if (rows.length > MAX_ROWS) rows = rows.slice(rows.length - MAX_ROWS);

    let serialized = JSON.stringify(rows);
    while (serialized.length > MAX_BYTES && rows.length > 1) {
      rows = rows.slice(Math.ceil(rows.length / 2));
      serialized = JSON.stringify(rows);
    }

    window.localStorage.setItem(OUTBOX_KEY, serialized);
  } catch {
    /* private mode / quota — the rows are lost, but the app is not */
  }
}

/**
 * Undo what JSON.stringify did to the row's typed fields.
 *
 * `timestamp` is the legacy device-clock field and is a `Date` at emit time;
 * serialised it becomes an ISO string, and a string written back to Firestore
 * is a string forever — invisible to every `timestamp` range query (the
 * `(message|userId|type, timestamp)` indexes and the legacy-era cutover
 * select). Measured on real dev data: 23 of 23 drained rows in one journey had
 * a string `timestamp` before this existed. `serverTs` needs no revival — the
 * drain re-stamps it.
 */
function reviveRow(row: Record<string, unknown>): Record<string, unknown> {
  const ts = row.timestamp;
  if (typeof ts === "string") {
    const parsed = new Date(ts);
    if (!isNaN(parsed.getTime())) return { ...row, timestamp: parsed };
  }
  return row;
}

/** Read and clear the outbox. Returns rows to re-send, with field types restored. */
export function takeOutbox(): PendingRow[] {
  const rows = readRaw();
  if (rows.length === 0) return [];
  try {
    window.localStorage.removeItem(OUTBOX_KEY);
  } catch {
    /* if we cannot clear it, better to drop the rows than re-send forever */
    return [];
  }
  return rows
    .filter(
      (r) => r && typeof r.id === "string" && r.row && typeof r.row === "object"
    )
    .map((r) => ({ id: r.id, row: reviveRow(r.row) }));
}

let listenersBound = false;

// Kept at module scope rather than inlined so they can be removed again. Only
// the test reset needs that today, but an anonymous listener is impossible to
// detach at all, which is a bad property for anything bound to `window`.
const onPageHide = (): void => persistPending();
const onVisibilityChange = (): void => {
  if (document.visibilityState === "hidden") persistPending();
};

/**
 * Bind the unload hooks. Idempotent.
 *
 * BOTH events are needed and neither is sufficient:
 *   - `pagehide` fires on real navigations and, on iOS, when the page enters the
 *     back/forward cache.
 *   - `visibilitychange` → hidden is the only thing that fires when a mobile OS
 *     backgrounds and later evicts the tab, which never produces a `pagehide`.
 * `beforeunload` is deliberately NOT used: it is unreliable on mobile and
 * registering it can disqualify the page from the bfcache.
 */
export function bindOutboxFlush(): void {
  if (listenersBound || typeof window === "undefined") return;
  listenersBound = true;
  try {
    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibilityChange);
  } catch {
    /* ignore */
  }
}

/**
 * Test-only. Detaches the listeners as well as clearing state: jsdom shares one
 * `window` across `jest.resetModules()`, so a previous module instance's
 * listeners would otherwise keep firing against ITS captured `pending` map and
 * pollute the next test's outbox.
 */
export function __resetOutboxForTest(): void {
  pending.clear();
  listenersBound = false;
  try {
    window.removeEventListener("pagehide", onPageHide);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.localStorage.removeItem(OUTBOX_KEY);
  } catch {
    /* ignore */
  }
}
